import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, ChevronDown, ChevronRight, FileQuestion } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { post, patch, del, get } from '@/lib/api';
import { useToast } from '@theonefamily/ui';

interface ReviewQuestion {
  id: string;
  question_text: string;
  question_type: 'rating' | 'text' | 'yes_no' | 'competency';
  rubric?: string;
  order: number;
  required: boolean;
}

interface ReviewCycle {
  id: string;
  name: string;
  cycle_type: string;
  period_start: string;
  period_end: string;
  self_review_deadline?: string;
  manager_review_deadline?: string;
  status: string;
  review_type: string;
  anonymous_peer_feedback: boolean;
}

const EMPTY_CYCLE_FORM = {
  name: '',
  cycle_type: 'annual' as const,
  period_start: '',
  period_end: '',
  self_review_deadline: '',
  manager_review_deadline: '',
  calibration_deadline: '',
  status: 'draft' as const,
  review_type: 'self_and_manager' as const,
  peer_reviewer_count: 3,
  anonymous_peer_feedback: false,
};

interface QuestionForm {
  question_text: string;
  question_type: 'rating' | 'text' | 'yes_no' | 'competency';
  rubric: string;
  order: number;
  required: boolean;
}

const EMPTY_QUESTION_FORM: QuestionForm = {
  question_text: '',
  question_type: 'rating',
  rubric: '',
  order: 0,
  required: true,
};

export function ReviewCyclesAdminPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [editingCycleId, setEditingCycleId] = useState<string | null>(null);
  const [cycleForm, setCycleForm] = useState(EMPTY_CYCLE_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedCycleId, setExpandedCycleId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionForm, setQuestionForm] = useState<QuestionForm>(EMPTY_QUESTION_FORM);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const { data, refetch } = useApi<{ cycles: ReviewCycle[] }>('admin/review-cycles');

  const cycles = data?.cycles ?? [];

  async function loadQuestions(cycleId: string) {
    setLoadingQuestions(true);
    try {
      const result = await get<{ questions: ReviewQuestion[] }>(`admin/review-cycles/${cycleId}/questions`);
      setQuestions(result.questions ?? []);
    } finally {
      setLoadingQuestions(false);
    }
  }

  async function toggleExpanded(cycleId: string) {
    if (expandedCycleId === cycleId) {
      setExpandedCycleId(null);
      setShowQuestionForm(false);
    } else {
      setExpandedCycleId(cycleId);
      setShowQuestionForm(false);
      await loadQuestions(cycleId);
    }
  }

  function openCreateCycle() {
    setCycleForm(EMPTY_CYCLE_FORM);
    setEditingCycleId(null);
    setShowCycleForm(true);
  }

  function openEditCycle(c: ReviewCycle) {
    setCycleForm({
      name: c.name,
      cycle_type: c.cycle_type as typeof EMPTY_CYCLE_FORM.cycle_type,
      period_start: c.period_start?.split('T')[0] ?? '',
      period_end: c.period_end?.split('T')[0] ?? '',
      self_review_deadline: c.self_review_deadline?.split('T')[0] ?? '',
      manager_review_deadline: c.manager_review_deadline?.split('T')[0] ?? '',
      calibration_deadline: '',
      status: c.status as typeof EMPTY_CYCLE_FORM.status,
      review_type: c.review_type as typeof EMPTY_CYCLE_FORM.review_type,
      peer_reviewer_count: 3,
      anonymous_peer_feedback: c.anonymous_peer_feedback,
    });
    setEditingCycleId(c.id);
    setShowCycleForm(true);
  }

  async function handleSaveCycle() {
    setSaving(true);
    try {
      const payload = {
        ...cycleForm,
        self_review_deadline: cycleForm.self_review_deadline || undefined,
        manager_review_deadline: cycleForm.manager_review_deadline || undefined,
        calibration_deadline: cycleForm.calibration_deadline || undefined,
      };
      if (editingCycleId) {
        await patch(`admin/review-cycles/${editingCycleId}`, payload);
      } else {
        await post('admin/review-cycles', payload);
      }
      setShowCycleForm(false);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCycle(id: string, name: string) {
    if (!confirm(`Delete review cycle "${name}"? Only draft cycles can be deleted.`)) return;
    try {
      await del(`admin/review-cycles/${id}`);
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cannot delete active review cycle.');
    }
  }

  async function handleSaveQuestion() {
    if (!expandedCycleId) return;
    setSaving(true);
    try {
      const payload = { ...questionForm, rubric: questionForm.rubric || undefined };
      if (editingQuestionId) {
        await patch(`admin/review-cycles/${expandedCycleId}/questions/${editingQuestionId}`, payload);
      } else {
        await post(`admin/review-cycles/${expandedCycleId}/questions`, { ...payload, order: questions.length });
      }
      setShowQuestionForm(false);
      setEditingQuestionId(null);
      await loadQuestions(expandedCycleId);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteQuestion(qid: string) {
    if (!expandedCycleId || !confirm('Remove this question?')) return;
    await del(`admin/review-cycles/${expandedCycleId}/questions/${qid}`);
    await loadQuestions(expandedCycleId);
  }

  const CYCLE_TYPE_LABELS: Record<string, string> = { annual: 'Annual', semi_annual: 'Semi-Annual', quarterly: 'Quarterly', ad_hoc: 'Ad-Hoc' };
  const REVIEW_TYPE_LABELS: Record<string, string> = { manager_only: 'Manager Only', '360': '360°', self_and_manager: 'Self + Manager' };
  const QUESTION_TYPE_LABELS: Record<string, string> = { rating: 'Rating (1-5)', text: 'Text', yes_no: 'Yes/No', competency: 'Competency' };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">Review Cycles</h2>
        <button onClick={openCreateCycle} className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> New Cycle
        </button>
      </div>

      {showCycleForm && (
        <Card className="border border-amber-500/30">
          <h3 className="text-sm font-semibold text-foreground mb-4">{editingCycleId ? 'Edit Review Cycle' : 'New Review Cycle'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Cycle Name *</label>
              <input value={cycleForm.name} onChange={e => setCycleForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Q1 2026 Performance Review" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Type</label>
              <select value={cycleForm.cycle_type} onChange={e => setCycleForm(f => ({ ...f, cycle_type: e.target.value as typeof cycleForm.cycle_type }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                {Object.entries(CYCLE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Status</label>
              <select value={cycleForm.status} onChange={e => setCycleForm(f => ({ ...f, status: e.target.value as typeof cycleForm.status }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                {['draft', 'active', 'in_review', 'calibration', 'complete'].map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Period Start *</label>
              <input type="date" value={cycleForm.period_start} onChange={e => setCycleForm(f => ({ ...f, period_start: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Period End *</label>
              <input type="date" value={cycleForm.period_end} onChange={e => setCycleForm(f => ({ ...f, period_end: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Self-Review Deadline</label>
              <input type="date" value={cycleForm.self_review_deadline} onChange={e => setCycleForm(f => ({ ...f, self_review_deadline: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Manager Review Deadline</label>
              <input type="date" value={cycleForm.manager_review_deadline} onChange={e => setCycleForm(f => ({ ...f, manager_review_deadline: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Review Type</label>
              <select value={cycleForm.review_type} onChange={e => setCycleForm(f => ({ ...f, review_type: e.target.value as typeof cycleForm.review_type }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                {Object.entries(REVIEW_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={cycleForm.anonymous_peer_feedback} onChange={e => setCycleForm(f => ({ ...f, anonymous_peer_feedback: e.target.checked }))} className="rounded" />
                Anonymous Peer Feedback
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSaveCycle} disabled={saving || !cycleForm.name || !cycleForm.period_start || !cycleForm.period_end} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving...' : editingCycleId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => setShowCycleForm(false)} className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-foreground text-sm rounded-lg transition-colors">Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {cycles.length === 0 && !showCycleForm && (
          <div className="text-center py-12 text-muted-foreground text-sm">No review cycles yet. Create one to start performance reviews.</div>
        )}
        {cycles.map(c => (
          <Card key={c.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleExpanded(c.id)} className="text-left flex items-center gap-2">
                    {expandedCycleId === c.id ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <span className="text-sm font-medium text-foreground">{c.name}</span>
                  </button>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground">{CYCLE_TYPE_LABELS[c.cycle_type]}</span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="flex gap-3 mt-1 text-xs text-muted-foreground ml-6">
                  <span>{c.period_start?.split('T')[0]} – {c.period_end?.split('T')[0]}</span>
                  <span>{REVIEW_TYPE_LABELS[c.review_type]}</span>
                  {c.anonymous_peer_feedback && <span>Anonymous peers</span>}
                </div>

                {expandedCycleId === c.id && (
                  <div className="mt-4 ml-6 border-t border-border pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <FileQuestion className="w-3.5 h-3.5" /> Review Questions {loadingQuestions ? '...' : `(${questions.length})`}
                      </h4>
                      <button onClick={() => { setQuestionForm({ ...EMPTY_QUESTION_FORM, order: questions.length }); setEditingQuestionId(null); setShowQuestionForm(true); }} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                        <Plus className="w-3 h-3" /> Add Question
                      </button>
                    </div>

                    {showQuestionForm && expandedCycleId === c.id && (
                      <div className="bg-surface-2 rounded-lg p-3 border border-border mb-3 space-y-2">
                        <textarea value={questionForm.question_text} onChange={e => setQuestionForm(f => ({ ...f, question_text: e.target.value }))} rows={2} className="w-full bg-surface-3 border border-border rounded px-2 py-1.5 text-sm text-foreground resize-none" placeholder="Question text *" />
                        <div className="flex gap-2">
                          <select value={questionForm.question_type} onChange={e => setQuestionForm(f => ({ ...f, question_type: e.target.value as typeof questionForm.question_type }))} className="flex-1 bg-surface-3 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                            {Object.entries(QUESTION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-foreground cursor-pointer">
                            <input type="checkbox" checked={questionForm.required} onChange={e => setQuestionForm(f => ({ ...f, required: e.target.checked }))} className="rounded" />
                            Required
                          </label>
                        </div>
                        {questionForm.question_type === 'competency' && (
                          <textarea value={questionForm.rubric} onChange={e => setQuestionForm(f => ({ ...f, rubric: e.target.value }))} rows={2} className="w-full bg-surface-3 border border-border rounded px-2 py-1.5 text-xs text-foreground resize-none" placeholder="Rubric description..." />
                        )}
                        <div className="flex gap-2">
                          <button onClick={handleSaveQuestion} disabled={saving || !questionForm.question_text} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-xs font-medium rounded transition-colors">
                            {saving ? 'Saving...' : editingQuestionId ? 'Update' : 'Add'}
                          </button>
                          <button onClick={() => setShowQuestionForm(false)} className="px-3 py-1.5 bg-surface-3 hover:bg-surface-2 text-foreground text-xs rounded transition-colors">Cancel</button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      {questions.sort((a, b) => a.order - b.order).map((q, i) => (
                        <div key={q.id} className="flex items-start gap-2 text-xs group">
                          <span className="w-5 h-5 rounded-full bg-surface-3 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                          <div className="flex-1">
                            <span className="text-foreground">{q.question_text}</span>
                            <span className="ml-2 text-muted-foreground">({QUESTION_TYPE_LABELS[q.question_type]})</span>
                            {q.required && <span className="ml-1 text-amber-500/70">*</span>}
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setQuestionForm({ question_text: q.question_text, question_type: q.question_type as QuestionForm['question_type'], rubric: q.rubric ?? '', order: q.order, required: q.required }); setEditingQuestionId(q.id); setShowQuestionForm(true); }} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => handleDeleteQuestion(q.id)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                      {questions.length === 0 && !loadingQuestions && <p className="text-xs text-muted-foreground">No questions yet.</p>}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEditCycle(c)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDeleteCycle(c.id, c.name)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
